import React from 'react';
import { Box, Button, ButtonBase, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import { useTranslation } from 'react-i18next';
import LastSql from './LastSql';
import { stageLabel } from './stageLabels';
import { cleanLlmText } from '../../../utilityFunctions/llmTextSanitizer';

/** Category-specific heading ("This metric isn't in your data"). */
export const diagnosisTitle = (t, diagnosis) => t(`Dashboards.diagnosis.title.${(diagnosis && diagnosis.category) || 'GENERIC'}`,
  { defaultValue: t('Dashboards.diagnosis.title.GENERIC') });

/** Diagnoses produced since plain-language failures carry a failure_code; older stored ones do not. */
export const isCurrentDiagnosis = (d) => !!(d && d.failure_code);

/**
 * The main text of a diagnosis. Legacy diagnoses (raw error text: "This widget was asked to: X (metric m).
 * … [validator] m: … Related data that exists: …") are tidied up while a fresh one loads.
 */
export const plainExplanation = (diagnosis) => {
  const text = cleanLlmText((diagnosis && diagnosis.explanation) || '');
  if (!text || isCurrentDiagnosis(diagnosis)) return text;
  return text
    .replace(/^This widget was asked to:\s*(.*?)(?:\s*\(metrics?\s+[^)]*\))?\.\s*/, (m, asked) => `We couldn’t build “${asked}”. `)
    .replace(/\s*Related data that exists:[^.]*\./, '')
    .replace(/\[[a-z][a-z0-9_]*\]\s*(?:[a-z][a-z0-9_]*:\s*)?/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

/** Localized chart-type name ("grouped_bar" → "Grouped bar chart"). */
export const vizLabel = (t, viz) => (viz
  ? t(`Dashboards.diagnosis.viz.${viz}`, { defaultValue: viz.replace(/_/g, ' ') })
  : null);

/**
 * 2–4 concrete charts the data supports, each one click away from rebuilding the widget:
 * title, chart type and one line on why it relates to the request.
 */
export function SuggestedCharts({ suggestions, disabled, onPick, selectedId, max = 4 }) {
  const { t } = useTranslation();
  if (!suggestions || !suggestions.length) return null;
  return (
    <Box sx={{ minWidth: 0 }} data-testid="suggested-charts">
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        {t('Dashboards.diagnosis.suggestionsTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        {suggestions.slice(0, max).map((s) => {
          const selected = selectedId === s.id;
          return (
            <ButtonBase key={s.id} disabled={disabled} onClick={() => onPick && onPick(s)} aria-pressed={selected}
              aria-label={s.label} title={s.hint}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 0.75, justifyContent: 'flex-start', textAlign: 'left',
                width: '100%', minWidth: 0, p: 0.75, borderRadius: 1, border: 1,
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'action.selected' : 'transparent',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                opacity: disabled ? 0.6 : 1,
              }}>
              <BarChartIcon fontSize="small" color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                  {s.viz && <Chip size="small" variant="outlined" label={vizLabel(t, s.viz)} sx={{ height: 18, fontSize: 11 }} />}
                </Box>
                {s.reason && (
                  <Typography variant="caption" color="text.secondary" display="block">{s.reason}</Typography>
                )}
              </Box>
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600, flexShrink: 0, mt: 0.25 }}>
                {selected ? t('Dashboards.diagnosis.selected') : t('Dashboards.diagnosis.buildThis')}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

/** "table (col, col, …)" lines of available_data — reference for experts writing their own fix. */
export function AvailableData({ data, maxTables = 3, maxColumns = 8 }) {
  const { t } = useTranslation();
  if (!data || !data.length) return null;
  return (
    <Box sx={{ minWidth: 0 }} data-testid="available-data">
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{t('Dashboards.diagnosis.availableData')}</Typography>
      {data.slice(0, maxTables).map((a) => (
        <Typography key={a.table} variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
          <Box component="span" sx={{ fontFamily: 'monospace' }}>{a.table}</Box>
          {a.description && ` — ${cleanLlmText(a.description)}`}
          {a.columns && a.columns.length > 0 && (
            <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: 11 }}>
              {`${a.columns.slice(0, maxColumns).join(', ')}${a.columns.length > maxColumns ? ', …' : ''}`}
            </Box>
          )}
        </Typography>
      ))}
    </Box>
  );
}

/** Error code, failed stage, raw error and last SQL, collapsed by default (for experts). */
export function TechnicalDetails({ stage, message, lastSql, code, defaultOpen = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(defaultOpen);
  if (!stage && !message && !lastSql && !code) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Button size="small" onClick={() => setOpen((o) => !o)} aria-expanded={open} sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}>
        {t('Dashboards.diagnosis.technicalDetails')}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 0.5, minWidth: 0, overflowWrap: 'anywhere' }} data-testid="technical-details">
          {code && (
            <Typography variant="caption" display="block">
              {t('Dashboards.diagnosis.errorCode')}{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{code}</Box>
            </Typography>
          )}
          {stage && (
            <Typography variant="caption" display="block">{t('Dashboards.repair.failedAt', { stage: stageLabel(t, stage) })}</Typography>
          )}
          {message && (
            <Typography variant="caption" color="error" display="block" sx={{ fontFamily: 'monospace' }}>
              {cleanLlmText(message)}
            </Typography>
          )}
          <LastSql sql={lastSql} maxHeight={140} />
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Guided failed-widget card (inside the widget cell, scrolls within it): category heading and a
 * plain-language explanation (what was asked, what went wrong, what to do next), then 2–4 charts
 * the data supports (one click rebuilds the widget as that chart), the data we found, and
 * "add details" (guided dialog), "Fix with my own knowledge" (expert dialog) and Retry — prominent
 * only for TECHNICAL. The raw error, its code and the SQL stay under a collapsed "Technical details".
 *
 * @param onAction({ type: 'retry' | 'guided' | 'expert' | 'suggestion', suggestion? })
 */
export default function GuidedFailureCard({
  diagnosis, diagnosisLoading, failure, canRepair, disabled, onAction,
}) {
  const { t } = useTranslation();
  const technical = diagnosis && diagnosis.category === 'TECHNICAL';
  const suggestions = (diagnosis && diagnosis.suggestions) || [];
  const questions = (diagnosis && diagnosis.questions) || [];
  const act = (a) => { if (onAction) onAction(a); };
  const explanation = diagnosis ? plainExplanation(diagnosis) : null;
  return (
    <Box data-testid="widget-failed"
      sx={{ p: 1, height: '100%', boxSizing: 'border-box', overflowY: 'auto', overflowX: 'hidden', minWidth: 0,
        overflowWrap: 'anywhere', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box>
        <Typography variant="subtitle2" color={technical ? 'error' : 'warning.dark'} sx={{ fontWeight: 700 }} data-testid="diagnosis-title">
          {diagnosis ? diagnosisTitle(t, diagnosis) : t('Dashboards.diagnosis.title.GENERIC')}
        </Typography>
        {diagnosisLoading && !isCurrentDiagnosis(diagnosis) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="diagnosis-loading">
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">{t('Dashboards.diagnosis.loading')}</Typography>
          </Box>
        )}
        {(explanation || !diagnosisLoading) && (
          <Typography variant="body2" sx={{ mt: 0.25 }} data-testid="diagnosis-explanation">
            {explanation || t('Dashboards.diagnosis.genericExplanation')}
          </Typography>
        )}
      </Box>
      {canRepair && (
        <SuggestedCharts suggestions={suggestions} disabled={disabled} max={4}
          onPick={(s) => act({ type: 'suggestion', suggestion: s })} />
      )}
      {canRepair && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {!technical && (
            <Button size="small" variant={suggestions.length ? 'outlined' : 'contained'} startIcon={<TipsAndUpdatesOutlinedIcon />}
              disabled={disabled} onClick={() => act({ type: 'guided' })} sx={{ textTransform: 'none' }}>
              {questions.length ? t('Dashboards.repair.guidedOpenQuestions', { count: questions.length })
                : t('Dashboards.repair.guidedOpen')}
            </Button>
          )}
          <Button size="small" variant={technical ? 'contained' : 'text'} startIcon={<ReplayIcon />} disabled={disabled}
            onClick={() => act({ type: 'retry' })} sx={{ textTransform: 'none' }}>
            {t('Dashboards.repair.retry')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<BuildOutlinedIcon />} disabled={disabled}
            onClick={() => act({ type: 'expert' })} sx={{ textTransform: 'none' }}>
            {t('Dashboards.repair.expert')}
          </Button>
        </Box>
      )}
      {diagnosis && <AvailableData data={diagnosis.available_data} maxTables={2} maxColumns={6} />}
      <TechnicalDetails stage={failure && failure.stage} message={failure && failure.message}
        lastSql={failure && failure.lastSql} code={diagnosis && diagnosis.failure_code} />
    </Box>
  );
}
